import React from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Avatar,
  InputBase,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Stack,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { styled } from "@mui/material/styles";
import { StyledCellHeader } from "../../../../components/StyledCellHeader";
import { RemoveRedEyeOutlined, Business, TrendingUp, Assessment, Inventory } from "@mui/icons-material";
import CrearProveedorDialog from "./crearproveedordialog";
import { fetchProveedores } from "../../../../utils/generalsFetch";
import { useNavigate } from "react-router-dom";
import AccessDeniedBox from '../../../../components/AccessDeniedBox';
import EmptyState from '../../../../components/EmptyState';

const providers = [
  {
    name: "Provider A",
    rnc: "12345678901",
    contact: "John Smith",
    email: "john.smith@example.com",
    phone: "809-555-1212",
    status: "Active",
    date: "2023-01-15",
  },
  {
    name: "Provider B",
    rnc: "98765432109",
    contact: "Jane Doe",
    email: "jane.doe@example.com",
    phone: "809-555-2323",
    status: "Inactive",
    date: "2023-02-20",
  },
  {
    name: "Provider C",
    rnc: "11223344556",
    contact: "Robert Brown",
    email: "robert.brown@example.com",
    phone: "809-555-3434",
    status: "Active",
    date: "2023-03-25",
  },
  {
    name: "Provider D",
    rnc: "66778899001",
    contact: "Emily White",
    email: "emily.white@example.com",
    phone: "809-555-4545",
    status: "Active",
    date: "2023-04-30",
  },
  {
    name: "Provider E",
    rnc: "22334455667",
    contact: "Michael Green",
    email: "michael.green@example.com",
    phone: "809-555-5656",
    status: "Inactive",
    date: "2023-05-05",
  },
  {
    name: "Provider F",
    rnc: "77889900112",
    contact: "Sarah Black",
    email: "sarah.black@example.com",
    phone: "809-555-6767",
    status: "Active",
    date: "2023-06-10",
  },
  {
    name: "Provider G",
    rnc: "33445566778",
    contact: "David Grey",
    email: "david.grey@example.com",
    phone: "809-555-7878",
    status: "Active",
    date: "2023-07-15",
  },
  {
    name: "Provider H",
    rnc: "88990011223",
    contact: "Laura Blue",
    email: "laura.blue@example.com",
    phone: "809-555-8989",
    status: "Inactive",
    date: "2023-08-20",
  },
];

const StatusChip = ({ status }) => (
  <Chip
    label={status}
    color={status === "Active" ? "success" : "default"}
    size="small"
    sx={{ minWidth: 80 }}
  />
);

const SearchPaper = styled(Paper)(({ theme }) => ({
  padding: "2px 8px",
  display: "flex",
  alignItems: "center",
  background: "#f0f2f4",
  minWidth: 200,
  maxWidth: 300,
  borderRadius: "8px",
  boxShadow: "none",
}));

export default function ProvidersTable() {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage] = React.useState(8);
  const [search, setSearch] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [proveedores, setProveedores] = React.useState([]);
  const [accessDenied, setAccessDenied] = React.useState(false);
  const navigate = useNavigate();
  
  React.useEffect(() => {
    fetchProveedores({ usuarioId: localStorage.getItem('id'), idcompany: localStorage.getItem('idcompany') }).then((data) => {
      setProveedores(data);
    }).catch((error) => {
      setAccessDenied(true);
    });
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const filtered = proveedores.filter((row) =>
    Object.values(row)
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  return (
    <Box sx={{ fontFamily: "Inter, Noto Sans, sans-serif", bgcolor: "#fff", minHeight: "100vh" }}>

      <CrearProveedorDialog open={open} onClose={()=>{setOpen(false)}}/>
      <Box >
        <Box display="flex" justifyContent="end" mb={2}>
          <Button
            variant="contained"
            color="primarybutton"
            startIcon={<AddIcon />}
            sx={{
              fontWeight: "bold",
              fontSize: 15,
              boxShadow: "none",
              minWidth: 120,
            }}
            onClick={()=>{setOpen(true)}}
          >
            Agregar Proveedor
          </Button>
        </Box>

        {!accessDenied ? 

        <>
        {/* Show empty state if no providers */}
        {proveedores.length === 0 ? (
            <EmptyState 
                icon={Business}
                title="No hay proveedores registrados"
                description="Comienza agregando tu primer proveedor para gestionar las relaciones comerciales y facilitar el proceso de compras."
                features={[
                    {
                        icon: Inventory,
                        title: "Gestión de Proveedores",
                        description: "Control completo de información y contactos"
                    },
                    {
                        icon: TrendingUp,
                        title: "Seguimiento de Compras",
                        description: "Historial y análisis de transacciones"
                    },
                    {
                        icon: Assessment,
                        title: "Reportes de Proveedores",
                        description: "Evaluación de rendimiento y costos"
                    }
                ]}
                buttonText="Agregar Primer Proveedor"
                onButtonClick={() => setOpen(true)}
                helpText="¿Necesitas ayuda? Contacta a soporte técnico para guiarte en el proceso"
            />
        ) : (
            <>
            <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: "none", minHeight: "500px" }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#fff" }}>
                    <StyledCellHeader>Name</StyledCellHeader>
                    <StyledCellHeader>RNC</StyledCellHeader>
                    <StyledCellHeader>Contact</StyledCellHeader>
                    <StyledCellHeader>Email</StyledCellHeader>
                    <StyledCellHeader>Phone</StyledCellHeader>
                    <StyledCellHeader>Status</StyledCellHeader>
                    <StyledCellHeader>Creation Date</StyledCellHeader>
                    <StyledCellHeader align="center"></StyledCellHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>{row.label}</TableCell>
                        <TableCell>{row.rnc}</TableCell>
                        <TableCell>{row.contacto}</TableCell>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>{row.telefono}</TableCell>
                        <TableCell>
                          <StatusChip status={row.estado} />
                        </TableCell>
                        <TableCell>{row.date}</TableCell>
                        <TableCell align="center">
                          <IconButton size="small" sx={{ color: "#617689" }} onClick={()=>{navigate(`/logistica/compras/proveedores/${row.value}`)}}>
                            <RemoveRedEyeOutlined/>
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <TablePagination
                component="div"
                count={filtered.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[8]}
                labelRowsPerPage=""
              />
            </Box>
            </>
        )}
        </>
        :
        <Box>
          <br></br>
          <br></br>
          <AccessDeniedBox
            title={'Acceso denegado'}
            message={'No tienes permisos para ver esta sección de Proveedores. Contacta a tu administrador para solicitar acceso o verifica tus roles y permisos.'}
          />
        </Box>
        }

      </Box>
    </Box>
  );
}
